import {useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type ClipboardEvent, type KeyboardEvent} from 'react'
import {useQuery} from '@tanstack/react-query'
import {X} from 'lucide-react'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {Label} from '@/components/ui/label'
import {Input} from '@/components/ui/input'
import {Button} from '@/components/ui/button'
import {Checkbox} from '@/components/ui/checkbox'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Badge} from '@/components/ui/badge'
import {Stack} from '@/components/shared/Stack'
import {FormField} from '@/components/shared/FormField'
import {FormGrid} from '@/components/shared/FormGrid'
import {MutedText} from '@/components/shared/MutedText'
import {InfoBanner} from '@/components/shared/InfoBanner'
import {InlineRow} from '@/components/shared/InlineRow'
import {InlineEmptyText} from '@/components/shared/InlineEmptyText'
import {SelectableOption} from '@/components/shared/SelectableOption'
import {Strong} from '@/components/shared/Strong'
import {Icon} from '@/components/shared/Icon'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {formatTimestamp} from '@/lib/date-utils'
import type {DayOfWeekToken, CronPreset} from './cron-utils'
import {
    PRESET_DAILY,
    buildWeeklyCron,
    buildMonthlyCron,
    detectPreset,
    extractWeeklyDays,
    extractMonthlyDay,
    isValidCron,
    nextRun,
} from './cron-utils'

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

const PRESET_VALUES = {
    NONE: 'NONE',
    DAILY: 'DAILY',
    WEEKLY: 'WEEKLY',
    MONTHLY: 'MONTHLY',
    CUSTOM: 'CUSTOM',
} as const

const DAY_TOKENS: DayOfWeekToken[] = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

function dayLabelFor(token: DayOfWeekToken, i18n: ReturnType<typeof useI18n>): string {
    switch (token) {
        case 'MON': return i18n.reporting.schedule.dayMon
        case 'TUE': return i18n.reporting.schedule.dayTue
        case 'WED': return i18n.reporting.schedule.dayWed
        case 'THU': return i18n.reporting.schedule.dayThu
        case 'FRI': return i18n.reporting.schedule.dayFri
        case 'SAT': return i18n.reporting.schedule.daySat
        case 'SUN': return i18n.reporting.schedule.daySun
    }
}

export interface ScheduleSectionProps {
    scheduleCron: string | null
    recipientIdentityIds: string[]
    recipientEmails: string[]
    notificationTemplateKey: string | null
    onChange: (next: {
        scheduleCron: string | null
        recipientIdentityIds: string[]
        recipientEmails: string[]
        notificationTemplateKey: string | null
    }) => void
    cronFieldError?: string | null
    disabled?: boolean
}

interface IdentityChip {
    id: string
    label: string
}

export function ScheduleSection({
    scheduleCron,
    recipientIdentityIds,
    recipientEmails,
    notificationTemplateKey,
    onChange,
    cronFieldError,
    disabled = false,
}: ScheduleSectionProps) {
    const i18n = useI18n()
    const services = useServices()

    const detected = useMemo<CronPreset>(() => detectPreset(scheduleCron), [scheduleCron])
    const [preset, setPreset] = useState<CronPreset>(detected)
    /*
     * WHY: when the user picks CUSTOM, they may intend a cron the detector also
     * recognises as a preset (e.g. an empty input ↔ "None"). We deliberately do
     * NOT auto-sync `preset` from `detected` while CUSTOM is selected — the
     * user's explicit choice wins until they pick a different preset.
     */
    useEffect(() => {
        if (preset !== PRESET_VALUES.CUSTOM) setPreset(detected)
    }, [detected, preset])

    const [customCron, setCustomCron] = useState<string>(scheduleCron ?? '')
    useEffect(() => {
        if (preset === PRESET_VALUES.CUSTOM) setCustomCron(scheduleCron ?? '')
    }, [scheduleCron, preset])

    const initialDays = useMemo<DayOfWeekToken[]>(
        () => (detected === PRESET_VALUES.WEEKLY && scheduleCron ? extractWeeklyDays(scheduleCron) : ['MON']),
        [detected, scheduleCron],
    )
    const [weeklyDays, setWeeklyDays] = useState<DayOfWeekToken[]>(initialDays)
    useEffect(() => setWeeklyDays(initialDays), [initialDays])

    const initialDom = useMemo<number>(
        () => (detected === PRESET_VALUES.MONTHLY && scheduleCron ? extractMonthlyDay(scheduleCron) : 1),
        [detected, scheduleCron],
    )
    const [monthlyDay, setMonthlyDay] = useState<number>(initialDom)
    useEffect(() => setMonthlyDay(initialDom), [initialDom])

    const [emailInput, setEmailInput] = useState('')
    const [emailError, setEmailError] = useState<string | null>(null)
    const [emailRejectedCount, setEmailRejectedCount] = useState(0)

    const [identityQuery, setIdentityQuery] = useState('')
    const identityInputRef = useRef<HTMLInputElement>(null)
    const [identitiesOpen, setIdentitiesOpen] = useState(false)

    const emitChange = useCallback(
        (next: Partial<{scheduleCron: string | null; recipientIdentityIds: string[]; recipientEmails: string[]; notificationTemplateKey: string | null}>) => {
            onChange({
                scheduleCron: next.scheduleCron !== undefined ? next.scheduleCron : scheduleCron,
                recipientIdentityIds: next.recipientIdentityIds !== undefined ? next.recipientIdentityIds : recipientIdentityIds,
                recipientEmails: next.recipientEmails !== undefined ? next.recipientEmails : recipientEmails,
                notificationTemplateKey: next.notificationTemplateKey !== undefined ? next.notificationTemplateKey : notificationTemplateKey,
            })
        },
        [onChange, scheduleCron, recipientIdentityIds, recipientEmails, notificationTemplateKey],
    )

    const handlePresetChange = useCallback(
        (next: string) => {
            const p = next as CronPreset
            setPreset(p)
            if (p === PRESET_VALUES.NONE) {
                emitChange({scheduleCron: null})
            } else if (p === PRESET_VALUES.DAILY) {
                emitChange({scheduleCron: PRESET_DAILY})
            } else if (p === PRESET_VALUES.WEEKLY) {
                emitChange({scheduleCron: buildWeeklyCron(weeklyDays)})
            } else if (p === PRESET_VALUES.MONTHLY) {
                emitChange({scheduleCron: buildMonthlyCron(monthlyDay)})
            } else {
                emitChange({scheduleCron: customCron.trim() === '' ? null : customCron.trim()})
            }
        },
        [emitChange, weeklyDays, monthlyDay, customCron],
    )

    const toggleDay = useCallback(
        (day: DayOfWeekToken, on: boolean) => {
            const next = on
                ? Array.from(new Set<DayOfWeekToken>([...weeklyDays, day]))
                : weeklyDays.filter(d => d !== day)
            const safe = next.length === 0 ? (['MON'] as DayOfWeekToken[]) : next
            setWeeklyDays(safe)
            emitChange({scheduleCron: buildWeeklyCron(safe)})
        },
        [weeklyDays, emitChange],
    )

    const handleMonthlyDay = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const raw = parseInt(e.target.value, 10)
            const clamped = Number.isNaN(raw) ? 1 : Math.min(31, Math.max(1, raw))
            setMonthlyDay(clamped)
            emitChange({scheduleCron: buildMonthlyCron(clamped)})
        },
        [emitChange],
    )

    const handleCustomCron = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value
            setCustomCron(val)
            emitChange({scheduleCron: val.trim() === '' ? null : val})
        },
        [emitChange],
    )

    const customValid = preset === PRESET_VALUES.CUSTOM ? (customCron.trim() === '' || isValidCron(customCron.trim())) : true

    const nextRunIso = useMemo(() => {
        if (!scheduleCron) return null
        const next = nextRun(scheduleCron)
        return next ? next.toISOString() : null
    }, [scheduleCron])

    const nextRunLabel = useMemo(() => {
        if (preset === PRESET_VALUES.NONE || !scheduleCron) return i18n.reporting.schedule.nextRunUnset
        if (!nextRunIso) return i18n.reporting.schedule.invalidCron
        return formatTimestamp(nextRunIso)
    }, [preset, scheduleCron, nextRunIso, i18n])

    const {data: identityResults, isLoading: identitiesLoading} = useQuery({
        queryKey: ['report-schedule-identity-search', identityQuery],
        queryFn: () => services.identities.list({q: identityQuery, size: 10}),
        enabled: identitiesOpen && identityQuery.length >= 2,
    })

    const {data: selectedIdentities} = useQuery({
        queryKey: ['report-schedule-selected-identities', recipientIdentityIds.join(',')],
        queryFn: async () => {
            const out: IdentityChip[] = []
            for (const id of recipientIdentityIds) {
                try {
                    const identity = await services.identities.detail(id)
                    out.push({
                        id,
                        label: identity.party?.displayName ?? identity.derivedDisplayName ?? id,
                    })
                } catch {
                    out.push({id, label: id})
                }
            }
            return out
        },
        enabled: recipientIdentityIds.length > 0,
    })

    const {data: templates} = useQuery({
        queryKey: ['report-schedule-templates'],
        queryFn: () => services.notificationTemplates.list({size: 100}),
    })

    const handleAddIdentity = useCallback(
        (id: string) => {
            if (recipientIdentityIds.includes(id)) return
            emitChange({recipientIdentityIds: [...recipientIdentityIds, id]})
            setIdentityQuery('')
            setIdentitiesOpen(false)
            identityInputRef.current?.focus()
        },
        [recipientIdentityIds, emitChange],
    )

    const handleRemoveIdentity = useCallback(
        (id: string) => emitChange({recipientIdentityIds: recipientIdentityIds.filter(x => x !== id)}),
        [recipientIdentityIds, emitChange],
    )

    const commitEmailString = useCallback(
        (raw: string): {accepted: number; rejected: number} => {
            const parts = raw.split(/[,\s]+/).map(s => s.trim()).filter(Boolean)
            if (parts.length === 0) return {accepted: 0, rejected: 0}
            let accepted = 0
            let rejected = 0
            const next = [...recipientEmails]
            for (const part of parts) {
                if (EMAIL_PATTERN.test(part)) {
                    if (!next.includes(part)) {
                        next.push(part)
                        accepted += 1
                    }
                } else {
                    rejected += 1
                }
            }
            if (accepted > 0) emitChange({recipientEmails: next})
            return {accepted, rejected}
        },
        [recipientEmails, emitChange],
    )

    const handleEmailKey = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',') {
                e.preventDefault()
                const trimmed = emailInput.trim()
                if (!trimmed) return
                if (!EMAIL_PATTERN.test(trimmed)) {
                    setEmailError(i18n.reporting.schedule.invalidEmail)
                    return
                }
                commitEmailString(trimmed)
                setEmailInput('')
                setEmailError(null)
                setEmailRejectedCount(0)
            }
        },
        [emailInput, commitEmailString, i18n],
    )

    const handleEmailPaste = useCallback(
        (e: ClipboardEvent<HTMLInputElement>) => {
            // eslint-disable-next-line i18next/no-literal-string -- ClipboardEvent.getData MIME-type identifier, not a user-visible string
            const text = e.clipboardData.getData('text')
            if (!text.includes(',') && !text.includes('\n')) return
            e.preventDefault()
            const {accepted, rejected} = commitEmailString(text)
            setEmailInput('')
            if (rejected > 0) setEmailRejectedCount(rejected)
            else setEmailRejectedCount(0)
            if (accepted === 0 && rejected > 0) setEmailError(i18n.reporting.schedule.invalidEmail)
            else setEmailError(null)
        },
        [commitEmailString, i18n],
    )

    const handleRemoveEmail = useCallback(
        (email: string) => emitChange({recipientEmails: recipientEmails.filter(x => x !== email)}),
        [recipientEmails, emitChange],
    )

    const handleTemplateChange = useCallback(
        (value: string) => {
            emitChange({notificationTemplateKey: value === 'DEFAULT' ? null : value})
        },
        [emitChange],
    )

    const emailErrorText = cronFieldError ? null : emailError

    return (
        <Card data-help-prefix="reportScheduleSection" data-testid="schedule-section">
            <CardHeader>
                <CardTitle data-help-key="_section" data-testid="schedule-section-title">{i18n.reporting.schedule.title}</CardTitle>
                <MutedText>{i18n.reporting.schedule.description}</MutedText>
            </CardHeader>
            <CardContent>
                <Stack gap="md">
                    <FormGrid columns={2}>
                        <FormField label={i18n.reporting.schedule.preset} htmlFor="schedule-preset">
                            <Select value={preset} onValueChange={handlePresetChange} disabled={disabled}>
                                <SelectTrigger id="schedule-preset" data-testid="select-schedule-preset">
                                    <SelectValue placeholder={i18n.reporting.schedule.preset}/>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={PRESET_VALUES.NONE} data-testid="opt-preset-none">{i18n.reporting.schedule.presetNone}</SelectItem>
                                    <SelectItem value={PRESET_VALUES.DAILY} data-testid="opt-preset-daily">{i18n.reporting.schedule.presetDaily}</SelectItem>
                                    <SelectItem value={PRESET_VALUES.WEEKLY} data-testid="opt-preset-weekly">{i18n.reporting.schedule.presetWeekly}</SelectItem>
                                    <SelectItem value={PRESET_VALUES.MONTHLY} data-testid="opt-preset-monthly">{i18n.reporting.schedule.presetMonthly}</SelectItem>
                                    <SelectItem value={PRESET_VALUES.CUSTOM} data-testid="opt-preset-custom">{i18n.reporting.schedule.presetCustom}</SelectItem>
                                </SelectContent>
                            </Select>
                        </FormField>

                        <FormField label={i18n.reporting.schedule.nextRun} htmlFor="schedule-next-run">
                            <MutedText size="sm" block data-testid="schedule-next-run">
                                {nextRunLabel}
                            </MutedText>
                        </FormField>
                    </FormGrid>

                    {preset === PRESET_VALUES.WEEKLY && (
                        <Stack gap="sm">
                            <Label>{i18n.reporting.schedule.daysOfWeek}</Label>
                            <InlineRow gap="sm" wrap>
                                {DAY_TOKENS.map(token => {
                                    const checked = weeklyDays.includes(token)
                                    // eslint-disable-next-line i18next/no-literal-string -- internal DOM id prefix, not user-visible
                                    const id = `dow-${token}`
                                    const dayLabel = dayLabelFor(token, i18n)
                                    return (
                                        <label key={token} htmlFor={id}>
                                            <InlineRow gap="xs" align="center">
                                                <Checkbox
                                                    id={id}
                                                    data-testid={`chk-day-${token.toLowerCase()}`}
                                                    checked={checked}
                                                    onCheckedChange={v => toggleDay(token, v === true)}
                                                    disabled={disabled}
                                                />
                                                <span>{dayLabel}</span>
                                            </InlineRow>
                                        </label>
                                    )
                                })}
                            </InlineRow>
                        </Stack>
                    )}

                    {preset === PRESET_VALUES.MONTHLY && (
                        <FormGrid columns={2}>
                            <FormField label={i18n.reporting.schedule.dayOfMonth} htmlFor="schedule-day-of-month">
                                <Input
                                    id="schedule-day-of-month"
                                    type="number"
                                    min={1}
                                    max={31}
                                    value={monthlyDay}
                                    onChange={handleMonthlyDay}
                                    disabled={disabled}
                                    data-testid="input-day-of-month"
                                />
                            </FormField>
                        </FormGrid>
                    )}

                    {preset === PRESET_VALUES.CUSTOM && (
                        <FormField
                            label={i18n.reporting.schedule.rawCron}
                            htmlFor="schedule-raw-cron"
                            error={cronFieldError ?? (customValid ? undefined : i18n.reporting.schedule.invalidCron)}
                        >
                            <Input
                                id="schedule-raw-cron"
                                data-testid="input-raw-cron"
                                value={customCron}
                                onChange={handleCustomCron}
                                placeholder={PRESET_DAILY}
                                disabled={disabled}
                            />
                            <MutedText size="xs">{i18n.reporting.schedule.rawCronHint}</MutedText>
                        </FormField>
                    )}

                    {cronFieldError && preset !== PRESET_VALUES.CUSTOM && (
                        <InfoBanner variant="destructive">{cronFieldError}</InfoBanner>
                    )}

                    <Stack gap="sm">
                        <Label htmlFor="schedule-identity-search">{i18n.reporting.schedule.recipientIdentities}</Label>
                        <Input
                            id="schedule-identity-search"
                            ref={identityInputRef}
                            data-testid="input-identity-search"
                            value={identityQuery}
                            onChange={e => {
                                setIdentityQuery(e.target.value)
                                setIdentitiesOpen(true)
                            }}
                            onFocus={() => setIdentitiesOpen(true)}
                            placeholder={i18n.reporting.schedule.recipientIdentitiesPlaceholder}
                            disabled={disabled}
                        />
                        {identitiesOpen && identityQuery.length >= 2 && (
                            <Card data-testid="identity-search-results">
                                {identitiesLoading && (
                                    <InlineEmptyText>{i18n.common.loading}</InlineEmptyText>
                                )}
                                {!identitiesLoading && (identityResults?.content ?? []).map(identity => (
                                    <SelectableOption
                                        key={identity.id}
                                        selected={false}
                                        onClick={() => handleAddIdentity(identity.id)}
                                        data-testid={`opt-identity-${identity.id}`}
                                    >
                                        <Strong>{identity.party?.displayName ?? identity.id}</Strong>
                                        <MutedText size="xs">{identity.identityType}</MutedText>
                                    </SelectableOption>
                                ))}
                                {!identitiesLoading && (identityResults?.content ?? []).length === 0 && (
                                    <InlineEmptyText>{i18n.common.noResults}</InlineEmptyText>
                                )}
                            </Card>
                        )}
                        {recipientIdentityIds.length === 0 ? (
                            <MutedText size="sm">{i18n.reporting.schedule.recipientIdentitiesEmpty}</MutedText>
                        ) : (
                            <InlineRow gap="xs" wrap>
                                {(selectedIdentities ?? recipientIdentityIds.map(id => ({id, label: id}))).map(chip => (
                                    <Badge variant="secondary" key={chip.id} data-testid={`chip-identity-${chip.id}`}>
                                        {chip.label}
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleRemoveIdentity(chip.id)}
                                            disabled={disabled}
                                            aria-label={i18n.reporting.schedule.removeRecipient}
                                            data-testid={`btn-remove-identity-${chip.id}`}
                                        >
                                            <Icon icon={X} size="xs"/>
                                        </Button>
                                    </Badge>
                                ))}
                            </InlineRow>
                        )}
                    </Stack>

                    <FormField
                        label={i18n.reporting.schedule.recipientEmails}
                        htmlFor="schedule-emails"
                        error={emailErrorText ?? undefined}
                    >
                        <Stack gap="sm">
                            <Input
                                id="schedule-emails"
                                data-testid="input-email-recipient"
                                value={emailInput}
                                onChange={e => {
                                    setEmailInput(e.target.value)
                                    if (emailError) setEmailError(null)
                                }}
                                onKeyDown={handleEmailKey}
                                onPaste={handleEmailPaste}
                                placeholder={i18n.reporting.schedule.emailPlaceholder}
                                disabled={disabled}
                            />
                            {emailRejectedCount > 0 && (
                                <MutedText size="sm" data-testid="email-rejected-summary">
                                    {i18n.reporting.schedule.emailRejectedSummary({count: emailRejectedCount})}
                                </MutedText>
                            )}
                            {recipientEmails.length > 0 && (
                                <InlineRow gap="xs" wrap>
                                    {recipientEmails.map(email => (
                                        <Badge variant="secondary" key={email} data-testid={`chip-email-${email}`}>
                                            {email}
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleRemoveEmail(email)}
                                                disabled={disabled}
                                                aria-label={i18n.reporting.schedule.removeRecipient}
                                                data-testid={`btn-remove-email-${email}`}
                                            >
                                                <Icon icon={X} size="xs"/>
                                            </Button>
                                        </Badge>
                                    ))}
                                </InlineRow>
                            )}
                        </Stack>
                    </FormField>

                    <FormField label={i18n.reporting.schedule.notificationTemplate} htmlFor="schedule-template">
                        <Select
                            value={notificationTemplateKey ?? 'DEFAULT'}
                            onValueChange={handleTemplateChange}
                            disabled={disabled}
                        >
                            <SelectTrigger id="schedule-template" data-testid="select-notification-template">
                                <SelectValue placeholder={i18n.reporting.schedule.defaultTemplate}/>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="DEFAULT" data-testid="opt-template-default">
                                    {i18n.reporting.schedule.defaultTemplate}
                                </SelectItem>
                                {(templates?.content ?? [])
                                    .filter(t => t.templateKey.startsWith('report-delivered'))
                                    .filter(t => t.templateKey !== 'report-delivered')
                                    .map(t => (
                                        <SelectItem
                                            key={t.templateKey}
                                            value={t.templateKey}
                                            data-testid={`opt-template-${t.templateKey}`}
                                        >
                                            {t.templateKey}
                                        </SelectItem>
                                    ))}
                            </SelectContent>
                        </Select>
                    </FormField>

                </Stack>
            </CardContent>
        </Card>
    )
}
